import { atom } from 'jotai';
import { atomWithReset } from 'jotai/utils';

import { createKmcPopupUrl, getKmcExpectedOrigin, getKmcPopupFeatures } from 'src/stores/kmc/utils';
import { getKmcRedirectUrl } from 'src/stores/register/kmc';
import { signUpSettingsLoadableAtom } from 'src/stores/register/signUpSettingsAtoms';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import getDeviceInfo from 'src/utils/device/info';
import { pushEvent } from 'src/utils/event-gateway';

// Keep the popup entry point because phone + CONSENT_B opens KMC before loginModalType
// moves to PHONE_NUMBER_LOGIN, so the postMessage handler cannot infer the downstream
// from the current modal type.
export type KmcEntryPoint = 'phone' | 'sns';

type KmcIdleState = {
  status: 'idle';
};

type KmcOpeningState = {
  status: 'opening';
  entryPoint: KmcEntryPoint;
  popupWindow: Window | null;
  expectedOrigin: string;
};

type KmcVerifiedState = {
  status: 'verified';
  entryPoint: KmcEntryPoint;
  kmcResultKey: string;
};

type KmcFallbackState = {
  status: 'fallback';
  entryPoint: KmcEntryPoint;
  // kmc_error: popup postMessage의 error 필드(KMC 자체 에러) → TSP fallback
  reason: 'popup_open_failed' | 'invalid_message' | 'kmc_error';
};

export type KmcState = KmcIdleState | KmcOpeningState | KmcVerifiedState | KmcFallbackState;

const initialKmcState: KmcState = {
  status: 'idle',
};

export const kmcStateAtom = atomWithReset<KmcState>(initialKmcState);

let preopenedKmcPopup: { entryPoint: KmcEntryPoint; popupWindow: Window } | null = null;

export const preopenKmcPopupAtom = atom(null, (_get, _set, entryPoint: KmcEntryPoint) => {
  const { isMobile } = getDeviceInfo();
  const popupWindow = window.open(
    'about:blank',
    `kmc_${Math.random().toString(36).substring(2)}`,
    getKmcPopupFeatures(isMobile)
  );

  if (!popupWindow) return;

  preopenedKmcPopup = {
    entryPoint,
    popupWindow,
  };
});

export const requestOpenKmcPopupAtom = atom(null, (get, set, entryPoint: KmcEntryPoint) => {
  const signUpSettingsLoadable = get(signUpSettingsLoadableAtom);
  const redirectUrl =
    signUpSettingsLoadable.state === 'hasData'
      ? getKmcRedirectUrl(signUpSettingsLoadable.data)
      : undefined;

  if (!redirectUrl) {
    throw new Error('KMC redirectUrl is required to open KMC popup');
  }

  const popupUrl = createKmcPopupUrl(redirectUrl, window.location.href);
  const popupWindow =
    preopenedKmcPopup?.entryPoint === entryPoint && !preopenedKmcPopup.popupWindow.closed
      ? preopenedKmcPopup.popupWindow
      : null;
  preopenedKmcPopup = null;

  if (popupWindow) {
    popupWindow.location.href = popupUrl;
  }

  const openFreshPopup = () => {
    const { isMobile } = getDeviceInfo();
    return window.open(
      popupUrl,
      `kmc_${Math.random().toString(36).substring(2)}`,
      getKmcPopupFeatures(isMobile)
    );
  };
  const resolvedPopupWindow = popupWindow ?? openFreshPopup();

  if (!resolvedPopupWindow) {
    set(kmcStateAtom, { status: 'fallback', entryPoint, reason: 'popup_open_failed' });
    return;
  }

  pushEvent({
    eventType: EVENT_TYPE.LOG,
    eventName: EVENT_NAME.VERIFICATION__IMPRESSION_KMC_WEBVIEW,
    eventParams: {
      action_category: 'impression',
      page: 'kmc',
      tab: 'verification',
      target: 'webview',
    },
  });

  set(kmcStateAtom, {
    status: 'opening',
    entryPoint,
    popupWindow: resolvedPopupWindow,
    expectedOrigin: getKmcExpectedOrigin(redirectUrl, window.location.href),
  });
});

export const setKmcVerifiedAtom = atom(null, (get, set, kmcResultKey: string) => {
  const kmcState = get(kmcStateAtom);
  if (kmcState.status === 'verified' || kmcState.status !== 'opening') return;

  set(kmcStateAtom, {
    status: 'verified',
    entryPoint: kmcState.entryPoint,
    kmcResultKey,
  });
});

export const setKmcFallbackAtom = atom(
  null,
  (get, set, reason: Extract<KmcFallbackState['reason'], 'invalid_message' | 'kmc_error'>) => {
    const kmcState = get(kmcStateAtom);
    if (kmcState.status !== 'opening') return;

    set(kmcStateAtom, {
      status: 'fallback',
      entryPoint: kmcState.entryPoint,
      reason,
    });
  }
);
